import React, { useState, useMemo } from 'react';
import { Palette, Copy, Check } from '../../components/icons';
import type { RGB } from '../../types';
import { hexToRgb, rgbToHex } from '../../utils/colors';
import { getContrastRatio } from '../../utils/colorAnalysis';
import {
  generateBrandPalette,
  MOOD_LABELS,
  INDUSTRY_LABELS,
  type Mood,
  type Industry,
} from '../../utils/brandPalette';
import Layout from '../../components/Layout';
import PageHeader from '../../components/PageHeader';

const MOODS: Mood[] = ['professional', 'playful', 'luxurious', 'natural', 'tech', 'bold'];
const INDUSTRIES: Industry[] = ['healthcare', 'finance', 'food', 'technology', 'fashion', 'education'];


const SwatchWithCopy: React.FC<{ hex: string; label: string; small?: boolean }> = ({ hex, label, small }) => {
  const [copied, setCopied] = useState(false);
  const textColor = getContrastRatio(hex, '#ffffff') > 3 ? '#ffffff' : '#1e293b';

  const handleCopy = () => {
    navigator.clipboard.writeText(hex);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      onClick={handleCopy}
      className={`group relative rounded-control border border-surface-border flex flex-col items-center justify-center transition-shadow hover:ring-2 hover:ring-brand-300 hover:ring-offset-2 ${small ? 'h-14' : 'h-20'}`}
      style={{ backgroundColor: hex }}
      title={`Copy ${hex}`}
    >
      <span className="font-mono text-xs font-bold opacity-80" style={{ color: textColor }}>
        {hex}
      </span>
      <span className="text-xs mt-0.5 opacity-60" style={{ color: textColor }}>
        {label}
      </span>
      <div className="absolute inset-0 rounded-lg bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        {copied ? <Check size={14} color="#fff" /> : <Copy size={14} color="#fff" />}
      </div>
    </button>
  );
};

const BrandBuilderPage: React.FC = () => {
  const [primaryRgb, setPrimaryRgb] = useState<RGB>({ r: 59, g: 130, b: 246 });
  const [mood, setMood] = useState<Mood>('professional');
  const [industry, setIndustry] = useState<Industry>('technology');

  const primaryHex = useMemo(() => rgbToHex(primaryRgb.r, primaryRgb.g, primaryRgb.b), [primaryRgb]);

  const palette = useMemo(
    () => generateBrandPalette(primaryRgb, mood, industry),
    [primaryRgb, mood, industry],
  );

  const handleHexInput = (value: string) => {
    const cleaned = value.replace('#', '');
    if (/^[0-9a-fA-F]{6}$/.test(cleaned)) {
      const rgb = hexToRgb('#' + cleaned);
      if (rgb) setPrimaryRgb(rgb);
    }
  };

  return (
    <Layout maxWidth="max-w-5xl">
          {/* Header */}
<PageHeader icon={Palette} title="Brand Palette Builder" description="Generate a complete brand color system from a single primary color." />

          {/* Controls */}
          <div className="ui-panel p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Primary Color */}
              <div>
                <label className="ui-label">Primary Color</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    aria-label="Primary color picker"
                    value={primaryHex}
                    onChange={(e) => {
                      const rgb = hexToRgb(e.target.value);
                      if (rgb) setPrimaryRgb(rgb);
                    }}
                    className="w-12 h-11 rounded-control border border-surface-border cursor-pointer"
                  />
                  <input
                    type="text"
                    aria-label="Primary HEX color"
                    defaultValue={primaryHex}
                    onBlur={(e) => handleHexInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleHexInput((e.target as HTMLInputElement).value)}
                    className="ui-input min-w-0 flex-1 font-mono"
                    placeholder="#FF6B6B"
                  />
                </div>
              </div>

              {/* Mood */}
              <div>
                <label className="ui-label">Mood</label>
                <div className="flex flex-wrap gap-1.5">
                  {MOODS.map((m) => (
                    <button
                      key={m}
                      aria-pressed={mood === m}
                      onClick={() => setMood(m)}
                      className={`min-h-11 px-3 py-2 rounded-control text-sm font-medium transition-colors ${
                        mood === m
                          ? 'bg-brand-600 text-white'
                          : 'bg-surface-sunken text-ink-secondary hover:bg-brand-50 hover:text-brand-600'
                      }`}
                    >
                      {MOOD_LABELS[m]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Industry */}
              <div>
                <label className="ui-label">Industry</label>
                <div className="flex flex-wrap gap-1.5">
                  {INDUSTRIES.map((ind) => (
                    <button
                      key={ind}
                      aria-pressed={industry === ind}
                      onClick={() => setIndustry(ind)}
                      className={`min-h-11 px-3 py-2 rounded-control text-sm font-medium transition-colors ${
                        industry === ind
                          ? 'bg-brand-600 text-white'
                          : 'bg-surface-sunken text-ink-secondary hover:bg-brand-50 hover:text-brand-600'
                      }`}
                    >
                      {INDUSTRY_LABELS[ind]}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Generated Palette */}
          <div className="ui-panel p-6 mb-6">
            <h2 className="text-lg font-bold text-ink mb-4">Brand Colors</h2>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
              <SwatchWithCopy hex={palette.primary} label="Primary" />
              <SwatchWithCopy hex={palette.secondary} label="Secondary" />
              <SwatchWithCopy hex={palette.accent} label="Accent" />
              <SwatchWithCopy hex={palette.neutral} label="Neutral" />
              <SwatchWithCopy hex={palette.text} label="Text" />
            </div>

            <h3 className="text-sm font-semibold text-ink-secondary mb-2">Semantic</h3>
            <div className="grid grid-cols-3 gap-3 mb-6">
              <SwatchWithCopy hex={palette.success} label="Success" small />
              <SwatchWithCopy hex={palette.warning} label="Warning" small />
              <SwatchWithCopy hex={palette.danger} label="Danger" small />
            </div>

            <h3 className="text-sm font-semibold text-ink-secondary mb-2">Surfaces</h3>
            <div className="grid grid-cols-3 gap-3 mb-6">
              <SwatchWithCopy hex={palette.background} label="Background" small />
              <SwatchWithCopy hex={palette.surface} label="Surface" small />
              <SwatchWithCopy hex={palette.text} label="Text" small />
            </div>

            <h3 className="text-sm font-semibold text-ink-secondary mb-2">Primary Scale</h3>
            <div className="flex gap-1">
              {[...palette.tints, ...palette.shades].map((hex, i) => (
                <button
                  key={i}
                  onClick={() => navigator.clipboard.writeText(hex)}
                  className="flex-1 h-8 rounded transition-transform hover:scale-y-125 border border-surface-border"
                  style={{ backgroundColor: hex }}
                  title={hex}
                />
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="ui-panel p-6">
            <h2 className="text-lg font-bold text-ink mb-4">Preview</h2>
            <div className="rounded-lg overflow-hidden border border-surface-border" style={{ backgroundColor: palette.background }}>
              {/* Mini landing page */}
              <div style={{ padding: '16px 20px', borderBottom: `1px solid ${palette.surface}` }} className="flex items-center justify-between">
                <div style={{ color: palette.primary, fontWeight: 700, fontSize: '14px' }}>Brand Name</div>
                <div style={{ backgroundColor: palette.primary, color: '#fff', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 600 }}>
                  Get Started
                </div>
              </div>
              <div style={{ padding: '32px 20px', textAlign: 'center' }}>
                <h2 style={{ color: palette.text, fontSize: '20px', fontWeight: 800, marginBottom: '8px' }}>
                  Your heading goes here
                </h2>
                <p style={{ color: palette.neutral, fontSize: '13px', maxWidth: '400px', margin: '0 auto 16px', lineHeight: 1.5 }}>
                  This is what your brand looks like with the generated palette applied to a real layout.
                </p>
                <div className="flex gap-2 justify-center">
                  <span style={{ backgroundColor: palette.primary, color: '#fff', padding: '8px 20px', borderRadius: '6px', fontSize: '12px', fontWeight: 600 }}>
                    Primary Action
                  </span>
                  <span style={{ backgroundColor: palette.secondary, color: '#fff', padding: '8px 20px', borderRadius: '6px', fontSize: '12px', fontWeight: 600 }}>
                    Secondary
                  </span>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', padding: '16px 20px' }}>
                {[
                  { color: palette.success, label: 'Active' },
                  { color: palette.warning, label: 'Pending' },
                  { color: palette.danger, label: 'Critical' },
                ].map(item => (
                  <div key={item.label} style={{ backgroundColor: palette.surface, borderRadius: '8px', padding: '12px', border: `1px solid ${palette.background}` }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: item.color, display: 'inline-block', marginRight: '6px' }} />
                    <span style={{ color: palette.text, fontSize: '12px', fontWeight: 600 }}>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
    </Layout>
  );
};

export default BrandBuilderPage;
